// Phase 5 documents (03, 04 §4.2 and §4.5): pickup requests, runs and their stops, driver cash
// custody and device heartbeats. All server-written.
import { z } from 'zod';
import {
  branchCodeSchema,
  sectorCodeSchema,
  serviceLineSchema,
  sizeCodeSchema,
  slugSchema,
} from './config';
import { addressSchema, e164Schema } from './cargo';

const minor = z.number().int().nonnegative().max(1e12);
const iso = z.string();

export const geoSchema = z
  .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    accuracyM: z.number().nonnegative().max(100_000).optional(),
  })
  .strict();

export const pickupWindowSchema = z
  .object({
    date: z.iso.date(),
    from: z.string().regex(/^\d{2}:\d{2}$/),
    to: z.string().regex(/^\d{2}:\d{2}$/),
  })
  .strict();

export const pickupWantsSchema = z
  .object({
    emptyBoxes: z
      .array(
        z.object({ sizeId: sizeCodeSchema, qty: z.number().int().positive().max(50) }).strict(),
      )
      .max(10)
      .default([]),
    cargoPickup: z.boolean().default(false),
    estimatedBoxes: z.number().int().nonnegative().max(200).default(0),
  })
  .strict();

/**
 * What the customer said they want to ship when they booked in the app (05 §5.3 Ship), kept on the
 * pickup request and copied to its stop so the dispatcher and the driver's intake can prefill the
 * collection. It is intent, not a booking: the price, the boxes and the receiver are confirmed at the door.
 */
export const pickupIntentSchema = z
  .object({
    service: z.enum(['sea', 'air', 'prime']).optional(),
    serviceLine: serviceLineSchema.optional(),
    destinationCityId: slugSchema.optional(),
    receiver: z
      .object({
        name: z.string().trim().min(1).max(120),
        phones: z.array(e164Schema).min(1).max(3),
        address: addressSchema,
        cityId: slugSchema,
      })
      .strict()
      .optional(),
    /** Boxes by size (sea and air). */
    boxes: z
      .array(z.object({ sizeId: sizeCodeSchema, qty: z.number().int().min(1).max(50) }).strict())
      .max(10)
      .optional(),
    /** Parcels by weight (Prime). */
    pieces: z
      .array(
        z
          .object({
            weightKg: z.number().positive().max(500),
            description: z.string().trim().max(200).default(''),
          })
          .strict(),
      )
      .max(20)
      .optional(),
    voucherCode: z
      .string()
      .regex(/^[A-Z0-9]{8,12}$/)
      .optional(),
    /** The estimate the customer saw, VAT included, so the office can compare it with the final price. */
    estimate: z
      .object({
        amountMinor: z.number().int().nonnegative().max(1e12),
        currency: z.string().regex(/^[A-Z]{3}$/),
      })
      .strict()
      .optional(),
  })
  .strict();
export type PickupIntent = z.infer<typeof pickupIntentSchema>;

export const PICKUP_STATUSES = [
  'requested',
  'scheduled',
  'assigned',
  'en_route',
  'completed',
  'failed',
  'cancelled',
] as const;

/** `pickupRequests/{pur_…}` */
export const pickupRequestDocSchema = z.object({
  id: z.string(),
  customerId: z.string().optional(),
  contact: z.object({ name: z.string(), phoneE164: e164Schema }).strict(),
  address: addressSchema,
  geo: geoSchema.optional(),
  zoneId: z.string().optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  window: pickupWindowSchema,
  wants: pickupWantsSchema,
  /** The customer's booking intent from the app (service, receiver, boxes, voucher, estimate). */
  intent: pickupIntentSchema.optional(),
  status: z.enum(PICKUP_STATUSES),
  runId: z.string().optional(),
  stopId: z.string().optional(),
  shipmentId: z.string().optional(),
  notes: z.string().default(''),
  source: z.enum(['office', 'phone', 'app', 'whatsapp', 'website']),
  campaignCode: z.string().optional(),
  failReason: z.string().optional(),
  cancelReason: z.string().optional(),
  /** The driver of the run the request is on (set when the run is assigned); drivers read by it. */
  driverUid: z.string().optional(),
  /** Failed visits so far. Two failures raise an alert (13). */
  failCount: z.number().int().nonnegative().default(0),
  /** The control tower looks at the request when this passes (13). */
  nextDueAt: iso.optional(),
  stateSince: iso,
  createdBy: z.string().optional(),
  createdAt: iso,
  completedAt: iso.optional(),
});

export const RUN_STATUSES = ['planned', 'assigned', 'active', 'closed'] as const;
export const STOP_STATUSES = ['pending', 'arrived', 'done', 'failed', 'skipped'] as const;
export const STOP_FAIL_REASONS = [
  'not_home',
  'wrong_address',
  'refused',
  'damaged',
  'unreachable',
  'other',
] as const;

/** `runs/{run_…}`: one driver's day route. */
export const runDocSchema = z.object({
  id: z.string(),
  type: z.enum(['pickup', 'delivery', 'mixed']),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  date: z.iso.date(),
  driverUid: z.string().optional(),
  driverName: z.string().default(''),
  vehicleId: z.string().optional(),
  vehiclePlate: z.string().default(''),
  zoneIds: z.array(z.string()).default([]),
  status: z.enum(RUN_STATUSES),
  counts: z
    .object({
      stops: z.number().int(),
      done: z.number().int(),
      failed: z.number().int(),
      boxesOut: z.number().int(),
      boxesDelivered: z.number().int(),
      boxesBack: z.number().int(),
      boxesMissing: z.number().int(),
    })
    .strict(),
  cashCollectedMinor: minor,
  currency: z.string(),
  lastGeo: geoSchema.extend({ at: iso }).optional(),
  /** A run should start by mid-morning on its date; the control tower looks when this passes (13). */
  startByAt: iso.optional(),
  nextDueAt: iso.optional(),
  /** Set by planning and cleared by the watcher: look at this run again. */
  watchDirty: z.boolean().optional(),
  stateSince: iso,
  startedAt: iso.optional(),
  closedAt: iso.optional(),
  closeNote: z.string().optional(),
  createdAt: iso,
});

/** `runs/{id}/stops/{stp_…}` */
export const stopDocSchema = z.object({
  id: z.string(),
  runId: z.string(),
  sequence: z.number().int().nonnegative(),
  kind: z.enum(['pickup', 'delivery']),
  pickupRequestId: z.string().optional(),
  shipmentId: z.string().optional(),
  expectedBarcodes: z.array(z.string()).default([]),
  label: z.string(),
  cityId: slugSchema.optional(),
  geo: geoSchema.optional(),
  /** Who to see and where, copied onto the stop so the phone needs nothing else offline. */
  contact: z
    .object({ name: z.string(), phones: z.array(e164Schema).default([]) })
    .strict()
    .optional(),
  address: addressSchema.optional(),
  /** Delivery stops: the boxes to hand over. Pickup stops: what to drop and take. */
  boxes: z
    .array(z.object({ barcode: z.string(), sizeId: z.string().optional() }).strict())
    .default([]),
  wants: pickupWantsSchema.optional(),
  /** The booking intent of the request, for the driver's intake to prefill. */
  intent: pickupIntentSchema.optional(),
  /** The driver of the run (set when assigned). */
  driverUid: z.string().optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  status: z.enum(STOP_STATUSES),
  collectMinor: minor.default(0),
  /** Failed visits to this stop. */
  attempts: z.number().int().nonnegative().default(0),
  outcome: z
    .object({
      receiverName: z.string().default(''),
      relationship: z.string().default(''),
      verification: z.enum(['code', 'id_photo', 'none']).optional(),
      photos: z.array(z.string()).default([]),
      signaturePath: z.string().default(''),
      geo: geoSchema.optional(),
      reason: z.enum(STOP_FAIL_REASONS).optional(),
      note: z.string().default(''),
      collectedMinor: minor.default(0),
      deliveredBarcodes: z.array(z.string()).default([]),
      byOffice: z.boolean().default(false),
    })
    .strict()
    .optional(),
  arrivedAt: iso.optional(),
  doneAt: iso.optional(),
});

/** `driverCustody/{branchId}__{uid}`: cash a driver holds until handed to a cashier (1001). */
export const driverCustodyDocSchema = z.object({
  id: z.string(),
  uid: z.string(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  currency: z.string(),
  heldMinor: z.number().int(),
  runIds: z.array(z.string()).default([]),
  driverName: z.string().default(''),
  /** When the balance last went above zero; the cash-overnight clock counts from it (13). */
  heldSince: iso.optional(),
  nextDueAt: iso.optional(),
  updatedAt: iso,
});

/**
 * `driverDirectory/{uid}`: the people who can be given a run, safe for dispatchers to list. The
 * staff collection is closed to everyone but a super admin, so this copy carries only a name, the
 * places the person works and whether they are active. Kept in step with `staff/{uid}` by the staff
 * functions; it holds everyone whose roles include `driver`.
 */
export const driverDirectoryDocSchema = z.object({
  uid: z.string(),
  name: z.string(),
  /** Branches the person's access names; empty when it is all of them or only whole sectors. */
  branchIds: z.array(z.string()).default([]),
  sectorIds: z.array(z.string()).default([]),
  /** Access is `all`: the person can work from any branch. */
  scopeAll: z.boolean().default(false),
  active: z.boolean(),
  updatedAt: iso,
});
export type DriverDirectoryDoc = z.infer<typeof driverDirectoryDocSchema>;

/** `driverCashHandovers/{dch_…}`: a cashier counted a driver's cash into a till (04 §4.5). */
export const driverCashHandoverDocSchema = z.object({
  id: z.string(),
  driverUid: z.string(),
  cashierUid: z.string(),
  tillId: z.string(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  currency: z.string(),
  heldMinor: z.number().int(),
  countedMinor: minor,
  varianceMinor: z.number().int(),
  runIds: z.array(z.string()).default([]),
  note: z.string().default(''),
  journalId: z.string().optional(),
  commandId: z.string(),
  at: iso,
});

export const TRANSFER_STATUSES = ['in_transit', 'received', 'received_short'] as const;

/** `emptyBoxTransfers/{xfr_…}`: empty boxes moving between two branches of one country. */
export const emptyBoxTransferDocSchema = z.object({
  id: z.string(),
  fromBranchId: branchCodeSchema,
  toBranchId: branchCodeSchema,
  /** The sending branch; the document is read where it is sent from and where it is going. */
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  items: z.array(z.object({ sizeId: sizeCodeSchema, qty: z.number().int().positive() }).strict()),
  received: z
    .array(z.object({ sizeId: sizeCodeSchema, qty: z.number().int().nonnegative() }).strict())
    .default([]),
  status: z.enum(TRANSFER_STATUSES),
  note: z.string().default(''),
  sentBy: z.string(),
  sentAt: iso,
  receivedBy: z.string().optional(),
  receivedAt: iso.optional(),
  commandId: z.string(),
});

/**
 * Input to the `reportDevice` callable (04 §4.7): a heartbeat from a phone or office computer.
 * `branchId` is the branch the person is working at; it may be left out when their access names
 * exactly one branch. A driver's phone may send `geo`, which moves the active run's last position.
 */
export const reportDeviceInputSchema = z
  .object({
    deviceId: z.string().regex(/^dev_[0-9A-HJKMNP-TV-Z]{26}$/),
    app: z.enum(['office', 'ops']),
    appVersion: z.string().min(1).max(40),
    pendingCount: z.number().int().nonnegative().max(1_000_000),
    lastSyncAt: z.iso.datetime({ offset: true }),
    branchId: branchCodeSchema.optional(),
    geo: geoSchema.optional(),
  })
  .strict();
export type ReportDeviceInput = z.infer<typeof reportDeviceInputSchema>;
